/**
 * @file useContactRows.ts
 * @description Строки разделов «Контакты» и «Соцсети» «Настроек сайта»
 * (phase19.16 Р1; SB18, SB85): один список `brand.contacts` на два раздела;
 * на сервер уходит только список, где все значения проходят проверку
 * реестра, иначе ошибочная соцсеть не дала бы сохранить остальное.
 */

import { useEffect, useRef, useState } from "react";

import type { PartFieldsApi } from "./usePartFields";
import { contactsForSave, PLAIN_KINDS, sameContacts, SOCIAL_KINDS, toRows, type ContactRow } from "../../lib/site-view/contacts-edit";
import type { SiteBrandDraft } from "../../lib/site-view/types";

export interface ContactRowsApi {
  rows: { plain: ContactRow[]; social: ContactRow[] };
  update: (next: { plain: ContactRow[]; social: ContactRow[] }) => void;
}

/** Строки обоих разделов из списка бренда (порядок внутри раздела сохраняется). */
function split(brand: Pick<SiteBrandDraft, "contacts">): { plain: ContactRow[]; social: ContactRow[] } {
  const rows = toRows(brand.contacts);
  return { plain: rows.filter((r) => PLAIN_KINDS.includes(r.kind)), social: rows.filter((r) => SOCIAL_KINDS.includes(r.kind)) };
}

/** Общее состояние строк разделов — на экране «Настройки сайта». */
export function useContactRows(brand: PartFieldsApi<SiteBrandDraft>): ContactRowsApi {
  const [rows, setRows] = useState(() => split(brand.fields));
  const { set } = brand;
  const setRef = useRef(set);
  setRef.current = set;

  // Список с сервера другой (после «Отменить изменения», чужой правки) —
  // строки пересобираются. Свой же список, вернувшийся с сервера, строки не
  // трогает: иначе поле теряло бы фокус посреди ввода. Пока в строках есть
  // ошибка, человек правит — не перебиваем.
  const contacts = brand.fields.contacts;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  useEffect(() => {
    const current = contactsForSave([...rowsRef.current.plain, ...rowsRef.current.social]);
    if (current === null || sameContacts(current, contacts)) return;
    setRows(split({ contacts }));
  }, [contacts]);

  function update(next: { plain: ContactRow[]; social: ContactRow[] }) {
    setRows(next);
    const list = contactsForSave([...next.plain, ...next.social]);
    if (list) setRef.current("contacts", list);
  }
  return { rows, update };
}

